import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useSession } from "@/app/api/authClient";
import { useAuthCapabilities } from "../api/edenClient";

export const isAuthRequired = () => import.meta.env?.VITE_REQUIRE_AUTH === "true";

export function useAuthGuard() {
  const { data: session, isPending } = useSession();
  const { data: authCapabilities, isLoading: isAuthCapabilitiesLoading } = useAuthCapabilities();
  // Auth is bypassed by default, only required when VITE_REQUIRE_AUTH=true
  const bypass = !isAuthRequired() || authCapabilities?.bypass;
  const _router = useRouter();

  const isAuthenticated = !!bypass || !!session;

  useEffect(() => {
    // Don't redirect while loading
    if (isPending || isAuthCapabilitiesLoading) return;

    // If not authenticated, redirect to signin with current path
    if (!isAuthenticated) {
      const currentPath = window.location.pathname + window.location.search;
      const searchParams = new URLSearchParams({ from: currentPath });
      window.location.href = `/auth/signin?${searchParams.toString()}`;
    }
  }, [isPending, isAuthCapabilitiesLoading, isAuthenticated]);

  return {
    session,
    isAuthenticated,
    isLoading: isPending || isAuthCapabilitiesLoading,
  };
} /**
 * Hook to check if the current user is an admin.
 * Admin status is determined server-side based on AUTH_SITE_ADMINS environment variable.
 */

export function useIsAdmin(): boolean | undefined {
  const { data: session } = useSession();

  // When auth is disabled, treat everyone as admin (development mode)
  if (!isAuthRequired()) {
    return true;
  }

  // Use isAdmin flag from session, which is set server-side
  // based on the AUTH_SITE_ADMINS environment variable
  return session?.user?.isAdmin ?? undefined; // undefined when loading, true if admin, false if not
}
